import { notification } from 'antd';
import { action, observable } from 'mobx';
import { ReactAI } from 'react-appinsights';
import {
    AguaInformacion,
    Municipio,
    Notification,
    OrganismoOperador,
    PredialInformacion,
    UsuarioPassword,
    ConsultaApertura
} from '../models';
import { Request } from '../utils/requests';
import { Request as RequestMocks } from '../utils/__mocks__/requests';
import { tokenStore } from './tokenStore';
import { EstadosRegistro } from '../models/Consultas';
import { UsuarioAlta } from '../models/Altas';

const endpoints = {
    agua: {
        base: 'registroimpuestosagua',
        subforms: ['ingresos', 'ingresosporuso', 'cuentas', 'descuentos']
    },
    predial: {
        base: 'registroimpuestospredial',
        subforms: ['ingresos', 'cuentas', 'descuentos']
    },
    seguridad: {
        base: 'seguridad',
        usuario: 'usuario',
        acciones: 'acciones',
        validar: '/validar',
        cambiarPassword: '/cambiarpassword',
        obtenerRoles: '/obtenerroles',
        obtenerCargos: '/obtenercargos',
        registrarUsuario: '/registrarusuario',
        obtenerUsuarioConsulta: '/obtenerusuarioconsulta',
        restablecerPassword: '/restablecerpassword',
        editarUsuario: '/actualizarUsuario',
        eliminarUsuario: '/BajaDeUsuario'
    },
    municipio: '/municipio',
    municipios: {
        base: 'municipio',
        municipiosSoapap: '/municipiossoapap'
    },
    organismooperador: '/organismooperador',
    aprobado: '/estado/aprobado',
    rechazado: '/estado/rechazado',
    enviado: '/estado/enviado',
    submit: '/registroimpuestos/estado/enviado',
    notificaciones: {
        base: '/notificaciones',
        usuario: '/usuario'
    },
    ingres: '/municipios',
    reportes: {
        base: '/reportes',
        consultaImpuestos: '/obtenerImpuestos',
        reportePdf: '/reportes',
        reporteExcel: '/reporteexcel',
        consultaMensual: '/reportecaptura',
        consultaAnexos: '/obteneranexos',
        notificacionLectura: '/reportenotificacionlectura',
        reporteMensualExcel: '/ReporteMensualExcel',
        listaUsuarios: '/listausuarios',
        responsivaUsuario: '/responsivausuario',
        reporteAnualPdf: '/reportesanuales'
    },
    impuestos: {
        base: '/apertura',
        obtenerAperturas: '/obteneraperturas',
        insertarAperturas: '/insertarapertura'
    }
};

export class RequestStore {
    @observable
    error: string = '';

    private httpRequest: Request;

    constructor() {
        this.httpRequest = new Request();
    }

    @action async getArchivo(fileName: string, notificacionId: number) {
        const endpoint = endpoints.notificaciones.base + `/${notificacionId}/archivos/${fileName}`;
        try {
            const response = await this.httpRequest.getArchivo(endpoint);
            return response;
        } catch (error) {
            this.mostrarError(error);
        }
    }

    @action async getReportePdf(tipoReporte: number, idFormato: number) {
        const endpoint = endpoints.reportes.base + endpoints.reportes.reportePdf;
        try {
            const response = await this.httpRequest.getReportePdf(endpoint, {
                tipoReporte,
                idFormato
            });
            return response;
        } catch (error) {
            if (error.response.status === 400) {
                try {
                    const response = await this.httpRequest.getReportePdfSinBuffer(endpoint, {
                        tipoReporte,
                        idFormato
                    });
                    return response;
                } catch (error) {
                    this.mostrarError(error);
                }
            } else {
                this.mostrarError(error);
            }
        }
    }

    @action async deleteArchivo(fileName: string, notificacionId: number) {
        const endpoint = endpoints.notificaciones.base + `/${notificacionId}/archivos/${fileName}`;
        try {
            const response = await this.httpRequest.deleteArchivo(endpoint);
            return response;
        } catch (error) {
            this.mostrarError(error);
        }
    }

    @action async putNotificaciones(notificacionId: number, notificacionObj: Notification) {
        const endpoint = endpoints.notificaciones.base + `/${notificacionId}`;
        try {
            const response = await this.httpRequest.putNotificaciones(endpoint, notificacionObj);
            return response;
        } catch (error) {
            this.mostrarError(error);
        }
    }

    @action async postNotificacion(params: any) {
        const endpoint = endpoints.notificaciones.base;
        try {
            const response = await this.httpRequest.postNotificacion(endpoint, params);
            return response.data as Notification;
        } catch (error) {
            this.mostrarError(error);
        }
    }

    @action async deleteNotification(notificacionId: number) {
        const endpoint = endpoints.notificaciones.base + `/${notificacionId}`;
        try {
            await this.httpRequest.deleteNotification(endpoint);
            return 'success';
        } catch (error) {
            this.mostrarError(error);
        }
        return 'failure';
    }

    @action async archivarNotification(notificacionId: number) {
        const endpoint = endpoints.notificaciones.base + `/${notificacionId}/archivar`;
        try {
            const response = await this.httpRequest.archivarNotification(endpoint);
            return response;
        } catch (error) {
            this.mostrarError(error);
        }
    }

    @action async getMunicipios() {
        const endpoint = endpoints.municipio;
        try {
            const response = await this.httpRequest.getMunicipio(endpoint);
            return response.data as Municipio[];
        } catch (error) {
            this.mostrarError(error);
        }
    }

    @action async getOrganismoOperador() {
        const endpoint = endpoints.organismooperador;
        try {
            // getMunicipio points to the municipio microservice - organismoOperado is the endpoint within that microservice
            const response = await this.httpRequest.getMunicipio(endpoint);
            return response.data as OrganismoOperador[];
        } catch (error) {
            this.mostrarError(error);
        }
    }

    @action async getEnviarForms(estado: number, idMunicipio: number) {
        const endpoint = endpoints.submit;
        const params = { estado, idMunicipio };
        try {
            const response = await this.httpRequest.getEnviado(endpoint, params);
            return response.data;
        } catch (error) {
            this.mostrarError(error);
        }
    }

    @action async getIngres() {
        const endpoint = endpoints.ingres;
        try {
            const response = await this.httpRequest.getIngres(endpoint);
            return response.data;
        } catch (error) {
            this.mostrarError(error);
        }
    }
    @action async getReintegro() {
        const endpoint = endpoints.municipio + '/obtenerreintegros';
        try {
            const response = await this.httpRequest.getReintegro(endpoint);
            return response.data;
        } catch (error) {
            this.mostrarError(error);
        }
    }

    @action async getForm(formType: 'predial' | 'agua', municipioId: number) {
        const endpoint = endpoints[formType].base;

        try {
            const response = await this.httpRequest.getNext(endpoint, municipioId);
            return response.data;
        } catch (error) {
            this.mostrarError(error);
        }
    }

    @action async getFromById(formId: number, formType: 'predial' | 'agua') {
        const endpoint = endpoints[formType].base + `/${formId}`;

        try {
            const response = await this.httpRequest.get(endpoint, formId);
            return response.data;
        } catch (error) {
            this.mostrarError(error);
        }
    }

    @action async fetchNewForm(formType: 'predial' | 'agua', municipioId: number) {
        const endpoint = endpoints[formType].base;

        try {
            const response = await this.httpRequest.post(endpoint, municipioId);
            return response.data;
        } catch (error) {
            this.mostrarError(error);
        }
    }

    @action async saveForm(
        form: AguaInformacion | PredialInformacion,
        formId: number,
        formType: 'agua' | 'predial',
        formNumber: number
    ) {
        const subformName = endpoints[formType].subforms[formNumber - 1];
        const endpoint = endpoints[formType].base + `/${formId}` + `/${subformName}`;

        try {
            await this.httpRequest.put(endpoint, form);
            this.mostrarNotificacion('exito', `Formato guardado con éxito`);
            return 'success';
        } catch (error) {
            this.mostrarError(error);
            return 'failure';
        }
    }

    @action async submitForm(formId: number, formType: 'agua' | 'predial', comentario: string) {
        const endpoint = endpoints[formType].base + `/${formId}` + endpoints.enviado;

        try {
            const response = await this.httpRequest.put(endpoint, { comentario });
            if (response !== null && response.data !== null) {
                return response.data;
            }
            this.mostrarNotificacion('exito', `El formato se ha enviado exitosamente`);
            return 'success';
        } catch (error) {
            this.mostrarError(error);
            return 'failure';
        }
    }

    @action async aprobarFormatos(
        formId: number,
        formType: 'agua' | 'predial',
        comentario: string
    ) {
        const endpoint = endpoints[formType].base + `/${formId}` + endpoints.aprobado;

        try {
            const response = await this.httpRequest.put(endpoint, { comentario });
            if (response !== null && response.data !== null) {
                return response.data;
            }
            this.mostrarNotificacion('informacion', `El formato ha sido aprobado`);
            return 'success';
        } catch (error) {
            this.mostrarError(error);
            return 'failure';
        }
    }

    @action async rechazarFormatos(
        formId: number,
        formType: 'agua' | 'predial',
        comentario: string
    ) {
        const endpoint = endpoints[formType].base + `/${formId}` + endpoints.rechazado;

        try {
            const response = await this.httpRequest.put(endpoint, { comentario });
            if (response !== null && response.data !== null) {
                return response.data;
            }
            this.mostrarNotificacion('informacion', `El formato ha sido rechazado`);
            return 'success';
        } catch (error) {
            this.mostrarError(error);
            return 'failure';
        }
    }

    @action async getUsuario() {
        await tokenStore!.validaTokenUsuario();
        const endpoint = endpoints.seguridad.base + '/' + endpoints.seguridad.usuario;
        try {
            const response = await this.httpRequest.getUsuario(endpoint);
            return response.data;
        } catch (error) {
            this.mostrarError(error);
        }
    }

    @action async getAcciones() {
        await tokenStore!.validaTokenUsuario();
        const endpoint = endpoints.seguridad.base + '/' + endpoints.seguridad.acciones;
        try {
            const response = await this.httpRequest.getAcciones(endpoint);
            return response.data;
        } catch (error) {
            this.mostrarError(error);
        }
    }

    @action async mostrarError(error: any) {
        if (typeof error.response === 'undefined' || !error.response.data.errors) {
            if (typeof error.response !== 'undefined') {
                if (error.response.status.toString() !== '401')
                    this.mostrarNotificacion('error', `${error.message}`);
            } else {
                this.mostrarNotificacion('error', `${error.message}`);
            }
        } else {
            if (error.response.data !== '') {
                Object.entries(error.response.data.errors).map(([key, value]) =>
                    Object.entries(value).map(([clave, mensaje]) =>
                        this.mostrarNotificacion('error', `${mensaje}`)
                    )
                );
            } else {
                this.mostrarNotificacion('error', `${error.message}`);
            }
        }
        // ReactAI.rootInstance.trackException(error);
    }

    @action async mostrarNotificacion(type: string, mensaje: string) {
        notification.config({ top: 80 });
        switch (type) {
            case 'exito':
                notification.success({
                    message: mensaje,
                    style: { backgroundColor: '#dff9be' }
                });
                // ReactAI.rootInstance.trackEvent({ name: mensaje }, { tipo: 'exito' });
                break;
            case 'error':
                notification.warning({
                    message: mensaje,
                    style: { backgroundColor: '#ffffc3' },
                    duration: 0
                });
                break;
            case 'informacion':
                notification.info({
                    message: mensaje,
                    style: { background: '#e6f7ff' }
                });
                // ReactAI.rootInstance.trackEvent({ name: mensaje }, { tipo: 'informacion' });
                break;
            default:
                return null;
        }
    }

    @action async getNotificacionesUsuario(rol: string, ejercicio: number) {
        const endpoint =
            endpoints.notificaciones.base +
            (rol.includes('ROO') || rol.includes('RM') ? endpoints.notificaciones.usuario : '');
        const params = { ejercicio };
        try {
            const response = await this.httpRequest.getNotificacionUsuario(endpoint, params);
            return response.data;
        } catch (error) {
            this.mostrarError(error);
        }
    }

    @action async getConsulta(params: any) {
        const endpoint = endpoints.reportes.base + endpoints.reportes.consultaImpuestos;
        try {
            const response = await this.httpRequest.getConsulta(endpoint, params);
            return response.data;
        } catch (error) {
            this.mostrarError(error);
        }
    }

    @action async getConsultaMensual(ejercicio: number, mes: number, municipio: number) {
        const endpoint = endpoints.reportes.base + endpoints.reportes.consultaMensual;
        const params = { ejercicio, mes, municipio };
        try {
            const response = await this.httpRequest.getConsultaMensual(endpoint, params);
            return response.data;
        } catch (error) {
            this.mostrarError(error);
        }
    }

    @action async getToken(iejdcqd: string, wefjiqwhdp: string) {
        const endpoint = endpoints.seguridad.base + endpoints.seguridad.validar;
        const params = { iejdcqd, wefjiqwhdp };
        let resultado: string = '';
        try {
            const response = await this.httpRequest.getToken(endpoint, params);
            return response.data;
        } catch (error) {
            if (error.response !== undefined) {
                resultado = error.response.statusText;
                switch (error.response.status) {
                    case 401:
                        if (error.response.statusText === 'Unauthorized') {
                            resultado = 'incorrecto';
                        } else {
                            resultado = 'expiro';
                        }
                        break;
                    case 500:
                        resultado = 'vacio';
                        break;
                    default:
                        resultado = 'indefinido';
                        break;
                }
            } else {
                resultado = 'indefinido';
            }
            return resultado;
        }
    }

    @action async validaTokenUsuario() {
        const endpoint = endpoints.seguridad.base + '/' + endpoints.seguridad.usuario;
        try {
            const response = await this.httpRequest.validaTokenUsuario(endpoint);
            return response.data;
        } catch (error) {
            return 'error';
        }
    }

    @action async updatePassword(usuarioPassword: UsuarioPassword) {
        const endpoint = endpoints.seguridad.base + endpoints.seguridad.cambiarPassword;
        try {
            const response = await this.httpRequest.updatePassword(endpoint, usuarioPassword);
            return response;
        } catch (error) {
            this.mostrarError(error);
        }
    }
    @action async getAnexos(tipo: number) {
        const endpoint = endpoints.reportes.base + endpoints.reportes.consultaAnexos;
        const params = { tipo };
        try {
            const response = await this.httpRequest.getAnexos(endpoint, params);
            return response.data;
        } catch (error) {
            return 'error';
        }
    }

    @action async getExcelAnexo(idAnexo: number, ejercicio: number) {
        const endpoint = endpoints.reportes.base + endpoints.reportes.reporteExcel;
        const params = { idAnexo, ejercicio };
        try {
            const response = await this.httpRequest.getExcelAnexo(endpoint, params);
            return response.data;
        } catch (error) {
            return 'error';
        }
    }

    @action async getReporteMensualExcel(ejercicio: number, mes: number, municipio: number) {
        const endpoint = endpoints.reportes.base + endpoints.reportes.reporteMensualExcel;
        const params = { ejercicio, mes, municipio };
        try {
            const response = await this.httpRequest.getReporteMensualExcel(endpoint, params);
            return response.data;
        } catch (error) {
            return 'error';
        }
    }

    @action async getReporteNotificacion(notificacionId: number) {
        const endpoint = endpoints.reportes.base + endpoints.reportes.notificacionLectura;
        try {
            const response = await this.httpRequest.getReporteNotificacion(endpoint, {
                notificacionId
            });
            return response;
        } catch (error) {
            this.mostrarError(error);
        }
    }

    @action async getConsultaUsuarios(municipio: string, rol: number) {
        const endpoint = endpoints.reportes.base + endpoints.reportes.listaUsuarios;
        const params = { municipio, rol };
        try {
            const response = await this.httpRequest.getConsultaUsuarios(endpoint, params);
            return response.data;
        } catch (error) {
            this.mostrarError(error);
        }
    }

    @action async getConsultaRoles() {
        const endpoint = endpoints.seguridad.base + endpoints.seguridad.obtenerRoles;
        try {
            const response = await this.httpRequest.getConsultaRoles(endpoint);
            return response.data;
        } catch (error) {
            this.mostrarError(error);
        }
    }

    @action async getResponsiva(idUsuario: number) {
        const endpoint = endpoints.reportes.base + endpoints.reportes.responsivaUsuario;
        try {
            const response = await this.httpRequest.getResponsiva(endpoint, {
                idUsuario
            });
            return response;
        } catch (error) {
            this.mostrarError(error);
        }
    }

    @action async getConsultaCargos() {
        const endpoint = endpoints.seguridad.base + endpoints.seguridad.obtenerCargos;
        try {
            const response = await this.httpRequest.getConsultaCargos(endpoint);
            return response.data;
        } catch (error) {
            this.mostrarError(error);
        }
    }

    @action async postUsuario(
        idRol: number,
        idCargo: number,
        idMunicipio: number,
        nombre: string,
        email: string,
        usuarioAcceso: string,
        password: string
    ) {
        const endpoint = endpoints.seguridad.base + endpoints.seguridad.registrarUsuario;
        const params = { idRol, idCargo, idMunicipio, nombre, email, usuarioAcceso, password };
        try {
            await this.httpRequest.postUsuario(endpoint, params);
            this.mostrarNotificacion('exito', `Usuario registrado con éxito`);
            return 'success';
        } catch (error) {
            this.mostrarError(error);
        }
    }

    @action async getUsuarioConsulta(idUsuarioConsulta: number) {
        const endpoint = endpoints.seguridad.base + endpoints.seguridad.obtenerUsuarioConsulta;
        const params = { idUsuarioConsulta };
        try {
            const response = await this.httpRequest.getUsuarioConsulta(endpoint, params);
            return response.data;
        } catch (error) {
            this.mostrarError(error);
        }
    }

    @action async putRestablecerPassword(idUsuario: number) {
        const endpoint = endpoints.seguridad.base + endpoints.seguridad.restablecerPassword;
        const params = { idUsuario };
        try {
            await this.httpRequest.putRestablecerPassword(endpoint, params);
            this.mostrarNotificacion('exito', `Contraseña restablecida con éxito`);
            return 'success';
        } catch (error) {
            this.mostrarError(error);
        }
    }

    @action async postEditarUsuario(id: number, idCargo: number, email: string) {
        const endpoint = endpoints.seguridad.base + endpoints.seguridad.editarUsuario;
        const params = { id, idCargo, email };
        try {
            await this.httpRequest.postEditarUsuario(endpoint, params);
            this.mostrarNotificacion('exito', `Usuario guardado con éxito`);
            return 'success';
        } catch (error) {
            this.mostrarError(error);
        }
    }

    @action async postEliminarUsuario(id: number) {
        const endpoint = endpoints.seguridad.base + endpoints.seguridad.eliminarUsuario;
        const params = { id };
        try {
            await this.httpRequest.postEliminarUsuario(endpoint, params);
            this.mostrarNotificacion('exito', `Usuario eliminado con éxito`);
            return 'success';
        } catch (error) {
            this.mostrarError(error);
        }
    }

    @action async getReporteAnualPdf(
        tipoReporte: number,
        ejercicio: number,
        municipioId: number,
        soapap: boolean
    ) {
        const endpoint = endpoints.reportes.base + endpoints.reportes.reporteAnualPdf;
        try {
            const response = await this.httpRequest.getReporteAnualPdf(endpoint, {
                tipoReporte,
                ejercicio,
                municipioId,
                soapap
            });
            return response;
        } catch (error) {
            if (error.response.status === 400) {
                try {
                    const response = await this.httpRequest.getReporteAnualSinBuffer(endpoint, {
                        tipoReporte,
                        ejercicio,
                        municipioId,
                        soapap
                    });
                    return response;
                } catch (error) {
                    this.mostrarError(error);
                }
            } else {
                this.mostrarError(error);
            }
        }
    }

    @action async getMunicipiosSoapap() {
        const endpoint = endpoints.municipios.base + endpoints.municipios.municipiosSoapap;
        try {
            const response = await this.httpRequest.getMunicipiosSoapap(endpoint);
            return response.data as Municipio[];
        } catch (error) {
            this.mostrarError(error);
        }
    }

    @action async getAperturas(ejercicio: number, mes: number) {
        const endpoint = endpoints.impuestos.base + endpoints.impuestos.obtenerAperturas;
        const params = { ejercicio, mes };
        try {
            const response = await this.httpRequest.getAperturas(endpoint, params);
            return response.data as ConsultaApertura[];
        } catch (error) {
            this.mostrarError(error);
        }
    }

    @action async postInsertarAperturas(listaAperturas: ConsultaApertura[]) {
        const endpoint = endpoints.impuestos.base + endpoints.impuestos.insertarAperturas;
        const params = { listaAperturas };
        try {
            await this.httpRequest.postInsertarAperturas(endpoint, params);
            this.mostrarNotificacion('exito', `Aperturas guardadas correctamente`);
            return 'success';
        } catch (error) {
            this.mostrarError(error);
        }
    }
}

export const requestStore = new RequestStore();
export default requestStore;
